/**
 * StatisticalReportTab
 *
 * Portfolio-level analytics: asset mix, geographic distribution, recovery rates.
 * CSS-only bar charts — no external charting dependency.
 */
import React, { useState, useCallback } from "react";
import { api } from "../../services/api.js";

interface CategoryRow { category: string; total: number; closed: number; default_rate: number; }
interface ZoneRow { zone: string; total: number; closed: number; }
interface PortfolioSummary { total: number; active: number; recovered: number; recovery_rate: number; }

const TH: React.CSSProperties = {
  padding: "10px 14px", textAlign: "left", fontWeight: 600,
  fontSize: 12, color: "var(--muted, #64748b)", textTransform: "uppercase",
  letterSpacing: "0.05em", borderBottom: "1px solid var(--border, #e2e8f0)",
  background: "var(--panel, #f8fafc)",
};
const TD: React.CSSProperties = {
  padding: "10px 14px", fontSize: 14, color: "var(--ink, #0f172a)",
  borderBottom: "1px solid var(--border, #e2e8f0)",
};

function DonutSlice({ label, value, total, color }: { label: string; value: number; total: number; color: string }): React.ReactElement {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div style={{ width: 12, height: 12, borderRadius: 2, background: color, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
          <span style={{ fontSize: 13, color: "var(--ink)" }}>{label}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{pct}% <span style={{ fontWeight: 400, color: "var(--muted)" }}>({value})</span></span>
        </div>
        <div style={{ height: 6, background: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.5s ease" }} />
        </div>
      </div>
    </div>
  );
}

const CATEGORY_COLORS = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#14b8a6"];
const ZONE_COLORS = ["#3b82f6", "#22c55e", "#eab308", "#f97316", "#ef4444", "#a855f7", "#06b6d4"];

export function StatisticalReportTab(): React.ReactElement {
  const now = new Date();
  const [from, setFrom] = useState(`${now.getFullYear()}-01`);
  const [to, setTo] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.reports.statistical({ from, to });
      setCategories(res.data.by_asset_category as unknown as CategoryRow[]);
      setZones(res.data.by_geographic_zone as unknown as ZoneRow[]);
      setSummary(res.data.portfolio_summary as PortfolioSummary);
      setLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  const totalCases = categories.reduce((s, c) => s + c.total, 0);
  const totalZones = zones.reduce((s, z) => s + z.total, 0);

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 24, flexWrap: "wrap" }}>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--muted)", marginBottom: 4 }}>From</label>
          <input type="month" value={from} onChange={(e) => setFrom(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid var(--border, #e2e8f0)", borderRadius: 6, fontSize: 14 }} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--muted)", marginBottom: 4 }}>To</label>
          <input type="month" value={to} onChange={(e) => setTo(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid var(--border, #e2e8f0)", borderRadius: 6, fontSize: 14 }} />
        </div>
        <button
          id="btn-run-statistical-report"
          onClick={() => { void fetch(); }}
          disabled={loading}
          style={{
            padding: "8px 20px", background: "var(--brand, #6366f1)", color: "#fff",
            border: "none", borderRadius: 6, cursor: loading ? "not-allowed" : "pointer",
            fontWeight: 600, fontSize: 14, opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? "Loading…" : "Run Report"}
        </button>
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: 14, color: "#991b1b", marginBottom: 20 }}>
          {error}
        </div>
      )}

      {loaded && (
        <>
          {/* Portfolio summary row */}
          {summary && (
            <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
              {[
                { label: "Total Cases", value: String(summary.total), color: "#6366f1" },
                { label: "Active", value: String(summary.active), color: "#0ea5e9" },
                { label: "Recovered", value: String(summary.recovered), color: "#10b981" },
                { label: "Recovery Rate", value: `${summary.recovery_rate}%`, color: "#f59e0b" },
              ].map((card) => (
                <div key={card.label} style={{
                  flex: 1, minWidth: 120,
                  background: "var(--panel, #f8fafc)",
                  border: `2px solid ${card.color}22`,
                  borderRadius: "var(--radius, 8px)",
                  padding: "16px 20px",
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: card.color, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>{card.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: "var(--ink, #0f172a)" }}>{card.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Two-column charts */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
            {/* Asset category distribution */}
            <div style={{ background: "var(--panel, #f8fafc)", border: "1px solid var(--border, #e2e8f0)", borderRadius: 8, padding: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", marginBottom: 16, marginTop: 0 }}>
                📦 Cases by Asset Category
              </h3>
              {categories.length === 0 ? (
                <p style={{ color: "var(--muted)", fontSize: 13 }}>No data</p>
              ) : (
                categories.map((c, i) => (
                  <DonutSlice key={c.category} label={c.category || "Unknown"} value={c.total} total={totalCases} color={CATEGORY_COLORS[i % CATEGORY_COLORS.length] ?? "#6366f1"} />
                ))
              )}
            </div>

            {/* Geographic zone distribution */}
            <div style={{ background: "var(--panel, #f8fafc)", border: "1px solid var(--border, #e2e8f0)", borderRadius: 8, padding: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", marginBottom: 16, marginTop: 0 }}>
                🗺 Cases by Geographic Zone
              </h3>
              {zones.length === 0 ? (
                <p style={{ color: "var(--muted)", fontSize: 13 }}>No data</p>
              ) : (
                zones.map((z, i) => (
                  <DonutSlice key={z.zone} label={z.zone || "Unknown"} value={z.total} total={totalZones} color={ZONE_COLORS[i % ZONE_COLORS.length] ?? "#3b82f6"} />
                ))
              )}
            </div>
          </div>

          {/* Category detail table */}
          {categories.length > 0 && (
            <>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)", marginBottom: 14 }}>Asset Category Breakdown</h3>
              <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid var(--border, #e2e8f0)", marginBottom: 24 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={TH}>Category</th>
                      <th style={TH}>Total Cases</th>
                      <th style={TH}>Recovered</th>
                      <th style={TH}>Active</th>
                      <th style={TH}>Default Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((c, i) => (
                      <tr key={c.category} style={{ background: i % 2 === 0 ? "#fff" : "var(--panel, #f8fafc)" }}>
                        <td style={{ ...TD, fontWeight: 500 }}>{c.category || "Unknown"}</td>
                        <td style={TD}>{c.total}</td>
                        <td style={{ ...TD, color: "#16a34a", fontWeight: 600 }}>{c.closed}</td>
                        <td style={TD}>{c.total - c.closed}</td>
                        <td style={TD}>
                          <span style={{ fontWeight: 600, color: c.default_rate > 50 ? "#dc2626" : c.default_rate > 20 ? "#d97706" : "#16a34a" }}>
                            {c.default_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {!loaded && !loading && (
        <div style={{ padding: 60, textAlign: "center", color: "var(--muted)" }}>
          Select a date range and click <strong>Run Report</strong> to view portfolio statistics.
        </div>
      )}
    </div>
  );
}
